import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, ArrowLeft, GraduationCap, Award, TrendingUp, Users, BookMarked, Lightbulb, ChevronDown, ChevronUp, ExternalLink, LayoutDashboard } from 'lucide-react';
import { usePageView } from '../utils/usePageView';
import { useUser } from '../UserContext';

const ResearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  // When opened from the cold-lander intro screen, render a "Back" arrow that
  // returns the visitor to /free-activity instead of the default home button.
  // The intro passes `state.fromTrialIntro` via react-router so refreshes lose
  // it (which is fine — they'd then just see the regular research page).
  const fromTrialIntro = !!location.state?.fromTrialIntro;
  // Two distinct page names so the dashboard can split the funnel — visitors
  // who reached /research from the cold-lander vs. organic /research traffic.
  usePageView(fromTrialIntro ? 'research-trial' : 'research');
  // Reset scroll on mount — react-router preserves the previous page's scroll
  // position by default, so a footer click on a long page would land you at
  // the bottom of /research.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedBibSection, setExpandedBibSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const keyFindings = [
    { icon: '📚', title: 'Early Literacy Matters', description: 'Children who struggle with reading by third grade are likely to continue struggling throughout school', source: 'Annie E. Casey Foundation (2010)' },
    { icon: '🏠', title: 'Home Environment Impact', description: 'Shared book reading has a significant positive effect on language, literacy, and reading outcomes', source: 'Bus, van IJzendoorn & Pellegrini (1995)' },
    { icon: '🎯', title: 'Phonemic Awareness', description: 'Systematic phonemic awareness instruction significantly improves reading and spelling outcomes', source: 'National Reading Panel (2000)' },
    { icon: '⏰', title: 'Critical Window', description: 'The optimal window for developing foundational literacy skills and language', source: 'Whitehurst & Lonigan (1998)' },
    { icon: '💬', title: 'Conversational Turns', description: 'Children with more conversational turns show significantly greater language-related brain activity', source: 'Romeo et al. (2018)' },
    { icon: '🎮', title: 'Play-Based Learning', description: 'Play-based learning supports cognitive, social, and literacy development across all domains', source: 'Hirsh-Pasek et al. (2020)' }
  ];

  const researchFoundations = [
    {
      id: 'nelp', title: 'National Early Literacy Panel (2008)', category: 'Meta-Analysis Foundation',
      color: 'from-blue-500 to-indigo-500',
      summary: 'Comprehensive meta-analysis identifying key predictors of later reading success',
      link: 'https://lincs.ed.gov/publications/pdf/NELPReport09.pdf',
      keyFindings: [
        'Alphabet knowledge is one of the strongest predictors of reading achievement',
        'Phonological awareness significantly predicts decoding ability',
        'Oral language skills directly impact reading comprehension',
        'Print knowledge and concepts about books matter from the earliest ages'
      ],
      applicationInBrightReading: [
        'Systematic alphabet introduction starting in Phase 2',
        'Progressive phonological awareness from rhyming (Phase 1-2) to segmentation (Phase 4)',
        'Rich vocabulary building through dialogic reading',
        'Explicit teaching of print concepts across all phases'
      ]
    },
    {
      id: 'nrp', title: 'National Reading Panel (2000)', category: 'Landmark Reading Research',
      color: 'from-rose-500 to-pink-500',
      summary: 'Evidence-based assessment identifying five pillars of reading instruction',
      link: 'https://www1.nichd.nih.gov/publications/pubs/nrp/documents/report.pdf',
      keyFindings: [
        'Phonemic awareness instruction significantly improves reading and spelling',
        'Systematic phonics is more effective than unsystematic phonics',
        'Fluency instruction bridges decoding and comprehension',
        'Comprehension strategies are teachable from an early age'
      ],
      applicationInBrightReading: [
        'Phase 2-3: Systematic phonemic awareness progression',
        'Phase 3-4: Systematic phonics with CVC patterns and word families',
        'Phase 4: Fluency practice through echo and repeated readings',
        'Phase 3-4: Comprehension strategies (prediction, retelling, sequencing)'
      ]
    },
    {
      id: 'conversational', title: 'Conversational Turns & Language Development', category: 'Language Input Quality',
      color: 'from-purple-500 to-pink-500',
      summary: 'Back-and-forth conversation matters more than word quantity',
      keyFindings: [
        'Conversational turns predict vocabulary by 24 months (Weisleder & Fernald, 2013)',
        'Conversational exposure activates Broca\'s area (Romeo et al., 2018)',
        'Early language in year 2 predicts skills at age 10 (Gilkerson et al., 2018)',
        'Quality of conversation matters more than quantity (Rowe, 2012)'
      ],
      applicationInBrightReading: [
        'Dialogic reading prompts encouraging child responses',
        'Open-ended questions modeled in parent scripts',
        'Turn-taking games embedded in every activity',
        'Emphasis on responsive parenting: follow child\'s lead'
      ]
    },
    {
      id: 'phonemic', title: 'Phonemic Awareness Research', category: 'Core Foundational Skill',
      color: 'from-green-500 to-teal-500',
      summary: 'The critical role of sound awareness in reading development',
      keyFindings: [
        'Children need to hear individual sounds before connecting them to letters (Adams, 1990)',
        'Phonological awareness develops progressively: word → syllable → phoneme (Anthony & Francis, 2005)',
        'Multi-sensory approaches enhance phonemic learning (Ehri et al., 2001)'
      ],
      applicationInBrightReading: [
        'Phase 1: Sound awareness through environmental sounds and rhythm',
        'Phase 2: Rhyming games, syllable clapping',
        'Phase 3: Onset-rime activities, sound isolation',
        'Phase 4: Full blending, segmentation, manipulation'
      ]
    },
    {
      id: 'dialogic', title: 'Dialogic Reading & Interactive Book Sharing', category: 'Language & Vocabulary',
      color: 'from-amber-500 to-orange-500',
      summary: 'Interactive reading strategies that build language and comprehension',
      link: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4422048/',
      keyFindings: [
        'Dialogic reading significantly increases vocabulary (Whitehurst & Lonigan, 1998)',
        'PEER sequence effective for language growth',
        'Quality of interaction matters more than reading time (Bus et al., 1995)'
      ],
      applicationInBrightReading: [
        'Parent prompts for open-ended questions during reading',
        'PEER sequence taught explicitly in parent instructions',
        'Guided retelling and story structure discussions in Phase 3-4'
      ]
    },
    {
      id: 'play', title: 'Play-Based Learning & Development', category: 'Pedagogical Approach',
      color: 'from-cyan-500 to-blue-500',
      summary: 'Evidence supporting learning through play for literacy development',
      link: 'https://cms.learningthroughplay.com/media/wmtlmbe0/learning-through-play_web.pdf',
      keyFindings: [
        'Play enhances cognitive, social, and emotional development (Hirsh-Pasek et al., 2020)',
        'Guided play balances child agency with adult support',
        'Multi-modal, hands-on activities enhance retention'
      ],
      applicationInBrightReading: [
        'All games designed as play experiences, not drills',
        'Parent-guided play with scaffolding',
        'Multi-sensory activities: movement, touch, sound throughout'
      ]
    },
    {
      id: 'scarborough', title: 'Scarborough\'s Reading Rope', category: 'Conceptual Framework',
      color: 'from-teal-500 to-cyan-500',
      summary: 'How component skills interweave for skilled reading',
      link: 'https://www.thereadingleague.org/wp-content/uploads/2023/11/TRLC-Educators-and-Specialists-The-Reading-Rope-Key-Ideas-Behind-the-Metaphor.pdf',
      keyFindings: [
        'Skilled reading requires both word recognition AND language comprehension',
        'Both strands must be taught explicitly and systematically',
        'Skills become increasingly automatic with practice'
      ],
      applicationInBrightReading: [
        'Dual focus: code-based skills AND meaning-based skills',
        'Spiral curriculum ensures skill recurrence and integration',
        'Both strands addressed in every activity'
      ]
    }
  ];

  const annotatedBibliography = [
    {
      category: "Language & Early Brain Development",
      sources: [
        { authors: "Kuhl, Patricia", title: "Brain Mechanisms in Early Language Acquisition", publication: "Neuron, 2010", annotation: "Shows that infants' brains are especially sensitive to speech sounds between 6–24 months. Supports early listening and sound-play activities in Phase 1." },
        { authors: "Weisleder, Adriana, and Anne Fernald", title: "Talking to Children Matters", publication: "Psychological Science, 2013", link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5510534/", annotation: "Babies who hear more child-directed speech process language faster and develop larger vocabularies by 24 months." },
        { authors: "Romeo, Rachel R., et al.", title: "Beyond the 30-Million-Word Gap", publication: "Psychological Science, 2018", link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5945324/", annotation: "Links conversational turns to neural activation in Broca's area. Conversation predicts verbal skills and brain function." },
        { authors: "Gilkerson, Jill, et al.", title: "Language Experience in the Second Year of Life Predicts Language and Academic Skills at Age 10", publication: "Pediatrics, 2018", link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6192025/", annotation: "10-year longitudinal study showing early language experience predicts later cognitive and language outcomes." },
        { authors: "Hart, Betty, and Todd Risley", title: "Meaningful Differences in the Everyday Experience of Young American Children", publication: "Brookes Publishing, 1995", annotation: "Classic research on how daily talk quantity affects children's vocabulary by age 3." },
        { authors: "Shonkoff, Jack P., and Deborah Phillips, eds.", title: "From Neurons to Neighborhoods", publication: "National Academies Press, 2000", annotation: "How everyday interactions shape brain wiring in the first three years." },
        { authors: "Hoff, Erika", title: "The Specificity of Environmental Influence: Socioeconomic Status Affects Early Vocabulary Development via Maternal Speech", publication: "Child Development, 2003", annotation: "SES affects vocabulary growth primarily through the quality and quantity of maternal speech, not other environmental factors." },
        { authors: "Fernald, Anne, and Virginia A. Marchman", title: "Individual Differences in Lexical Processing at 18 Months Predict Vocabulary Growth in Typically Developing and Late-Talking Toddlers", publication: "Child Development, 2012", annotation: "Processing speed at 18 months predicts vocabulary growth, showing that early language efficiency is a key indicator of development." }
      ]
    },
    {
      category: "Book Reading & Story Exposure",
      sources: [
        { authors: "Raikes, Helen, et al.", title: "Mother-Child Bookreading in Low-Income Families", publication: "Child Development, 2006", annotation: "Early shared reading correlates with larger vocabularies and stronger cognitive development." },
        { authors: "Kalb, Guyonne, and van Ours, Jan C.", title: "Reading to Young Children: A Head-Start in Life?", publication: "Economics of Education Review, 2014", annotation: "Reading to children at young ages significantly improves later reading, cognitive, and behavioral outcomes." },
        { authors: "Bus, Adriana G., et al.", title: "Joint Book Reading Makes for Success in Learning to Read", publication: "Review of Educational Research, 1995", annotation: "Meta-analysis: shared book reading consistently improves vocabulary and comprehension." },
        { authors: "Mol, Saskia E., and Adriana G. Bus", title: "To Read or Not to Read: A Meta-Analysis of Print Exposure", publication: "Psychological Bulletin, 2011", annotation: "Early print exposure builds vocabulary, grammar, and school readiness." },
        { authors: "Whitehurst, Grover J., et al.", title: "Reading Aloud in the Home and School Context", publication: "Journal of Educational Psychology, 1999", annotation: "Dialogic reading boosts language and reasoning skills more than passive reading." },
        { authors: "Cunningham, Anne E., and Keith E. Stanovich", title: "Early Reading Acquisition and Its Relation to Reading Experience and Ability 10 Years Later", publication: "Developmental Psychology, 1997", annotation: "First-grade reading ability predicts reading volume and vocabulary 10 years later, underscoring the long-term impact of early literacy." },
        { authors: "Sénéchal, Monique, and Jo-Anne LeFevre", title: "Parental Involvement in the Development of Children's Reading Skill: A Five-Year Longitudinal Study", publication: "Child Development, 2002", annotation: "Home literacy activities like shared reading and teaching letters directly contribute to children's reading development over five years." }
      ]
    },
    {
      category: "Phonological Awareness & Sound Skills",
      sources: [
        { authors: "Saffran, Jenny R., et al.", title: "Statistical Learning by 8-Month-Old Infants", publication: "Science, 1996", link: "https://www.science.org/doi/10.1126/science.274.5294.1926", annotation: "Infants can extract patterns from speech through statistical learning." },
        { authors: "Anthony, Jason L., and David J. Francis", title: "Development of Phonological Awareness", publication: "Current Directions in Psychological Science, 2005", annotation: "Developmental progression: word → syllable → onset-rime → phoneme." },
        { authors: "Lonigan, Christopher J., et al.", title: "Development of Phonological Sensitivity in 2- to 5-Year-Old Children", publication: "Journal of Educational Psychology, 2000", annotation: "Even toddlers benefit from simple sound-play activities." },
        { authors: "Carson, Kerry, et al.", title: "Classroom Phonological Awareness Instruction and Literacy Outcomes", publication: "Language, Speech, and Hearing Services in Schools, 2013", annotation: "PA + alphabet instruction effective in preschool before formal reading." },
        { authors: "Gillon, Gail T.", title: "Phonological Awareness: From Research to Practice", publication: "Guilford Press, 2018", annotation: "Evidence-based phonemic awareness strategies for preschoolers." }
      ]
    },
    {
      category: "Vocabulary & Oral Language",
      sources: [
        { authors: "Beck, Isabel L., et al.", title: "Bringing Words to Life: Robust Vocabulary Instruction", publication: "Guilford Press, 2013", annotation: "Practical vocabulary-building through repeated exposure and contextual learning." },
        { authors: "Dickinson, David K., and Patton O. Tabors", title: "Beginning Literacy with Language", publication: "Brookes Publishing, 2001", annotation: "Rich early conversations predict reading comprehension in elementary school." },
        { authors: "Pan, Barbara A., et al.", title: "Maternal Talk and Child Vocabulary Growth", publication: "Child Development, 2005", annotation: "Responsive conversational turn-taking predicts faster word growth in toddlers." },
        { authors: "Storch, Stacey A., and Grover J. Whitehurst", title: "Oral Language and Code-Related Precursors to Reading: Evidence from a Longitudinal Structural Model", publication: "Developmental Psychology, 2002", annotation: "Oral language and code-related skills (letters, phonics) follow distinct but interconnected developmental pathways to reading." },
        { authors: "Tomasello, Michael", title: "Constructing a Language: A Usage-Based Theory of Language Acquisition", publication: "Harvard University Press, 2003", annotation: "Children learn language through social interaction and pattern-finding, not innate grammar — supports conversational, play-based teaching." },
        { authors: "Rowe, Meredith L.", title: "A Longitudinal Investigation of the Role of Quantity and Quality of Child-Directed Speech in Vocabulary Development", publication: "Child Development, 2012", annotation: "Quality of parent speech (diversity, complexity) matters more than sheer quantity for vocabulary growth." }
      ]
    },
    {
      category: "Play, Cognitive & Social Foundations",
      sources: [
        { authors: "Hirsh-Pasek, Kathy, et al.", title: "Learning Through Play: A Review of the Evidence", publication: "LEGO Foundation, 2020", link: "https://cms.learningthroughplay.com/media/wmtlmbe0/learning-through-play_web.pdf", annotation: "Play enhances cognitive, social, emotional, and literacy development." },
        { authors: "Hirsh-Pasek, Kathy, et al.", title: "Putting Education in 'Educational' Apps", publication: "Psychological Science in the Public Interest, 2015", annotation: "Apps encouraging parent-child interaction are most effective. Passive screen time doesn't help." },
        { authors: "Vygotsky, Lev S.", title: "Mind in Society", publication: "Harvard University Press, 1978", annotation: "Zone of Proximal Development: learning through guided interaction." }
      ]
    },
    {
      category: "Early Literacy & Reading Readiness",
      sources: [
        { authors: "National Early Literacy Panel", title: "Developing Early Literacy", publication: "National Institute for Literacy, 2008", link: "https://lincs.ed.gov/publications/pdf/NELPReport09.pdf", annotation: "Landmark meta-analysis identifying strongest predictors of reading success." },
        { authors: "National Reading Panel", title: "Teaching Children to Read", publication: "NICHD, 2000", link: "https://www1.nichd.nih.gov/publications/pubs/nrp/documents/report.pdf", annotation: "Five pillars: phonemic awareness, phonics, fluency, vocabulary, comprehension." },
        { authors: "Adams, Marilyn Jager", title: "Beginning to Read", publication: "MIT Press, 1990", annotation: "Phonemic awareness, print exposure, and vocabulary together predict reading success." },
        { authors: "Snow, Catherine E., et al.", title: "Preventing Reading Difficulties in Young Children", publication: "National Academies Press, 1998", annotation: "Early intervention before age 5 is key to preventing future reading struggles." },
        { authors: "IES / What Works Clearinghouse", title: "Foundational Skills to Support Reading", publication: "IES Practice Guide, 2016", link: "https://ies.ed.gov/ncee/wwc/docs/practiceguide/wwc_foundationalreading_070516.pdf", annotation: "Actionable recommendations for phonics scope and daily practice." },
        { authors: "Stanovich, Keith E.", title: "Matthew Effects in Reading: Some Consequences of Individual Differences in the Acquisition of Literacy", publication: "Reading Research Quarterly, 1986", annotation: "Children who read well read more, gaining vocabulary and knowledge; struggling readers fall further behind — the rich get richer." },
        { authors: "Juel, Connie", title: "Learning to Read and Write: A Longitudinal Study of 54 Children from First Through Fourth Grades", publication: "Journal of Educational Psychology, 1988", annotation: "Children who were poor readers at the end of first grade had an 88% chance of remaining poor readers by fourth grade." }
      ]
    },
    {
      category: "Alphabet, Letters & Print Concepts",
      sources: [
        { authors: "Treiman, Rebecca, and Vicki Broderick", title: "What's in a Name", publication: "Cognition, 1998", annotation: "Letter-name knowledge facilitates letter-sound learning." },
        { authors: "Piasta, Shayne B., and Richard K. Wagner", title: "Learning Letter Names and Sounds", publication: "Journal of Experimental Child Psychology, 2010", annotation: "Multi-modal learning enhances letter retention." },
        { authors: "Ehri, Linnea C.", title: "Learning to Read Words", publication: "Scientific Studies of Reading, 2005", annotation: "How children form visual-phonological connections for word recognition." },
        { authors: "Justice, Laura M., and Helen K. Ezell", title: "Use of Storybook Reading to Increase Print Awareness in At-Risk Children", publication: "American Journal of Speech-Language Pathology, 2002", annotation: "Explicit print referencing during storybook reading significantly increases print awareness in at-risk preschoolers." }
      ]
    },
    {
      category: "Decoding, Phonics & Word Reading",
      sources: [
        { authors: "Ehri, Linnea C., et al.", title: "Phonemic Awareness Instruction Helps Children Learn to Read", publication: "Reading Research Quarterly, 2001", link: "https://ila.onlinelibrary.wiley.com/doi/10.1598/RRQ.36.3.2", annotation: "Phonemic awareness instruction linked to letters significantly improves reading." },
        { authors: "Goswami, Usha", title: "Phonology, Reading Development, and Dyslexia", publication: "Annals of Dyslexia, 2002", annotation: "Word families facilitate pattern recognition and decoding by analogy." },
        { authors: "Blachman, Benita A., et al.", title: "Road to the Code", publication: "Brookes Publishing, 2000", annotation: "Sound blending and segmentation are teachable and predict decoding success." }
      ]
    },
    {
      category: "Comprehension, Fluency & Dialogic Reading",
      sources: [
        { authors: "Scarborough, Hollis S.", title: "The Reading Rope", publication: "Handbook of Early Literacy Research, 2001", link: "https://www.thereadingleague.org/wp-content/uploads/2023/11/TRLC-Educators-and-Specialists-The-Reading-Rope-Key-Ideas-Behind-the-Metaphor.pdf", annotation: "Component skills must interweave for skilled reading." },
        { authors: "Kuhn, Melanie R., and Steven A. Stahl", title: "Fluency: A Review of Developmental and Remedial Practices", publication: "Journal of Educational Psychology, 2003", annotation: "Fluency bridges decoding and comprehension." },
        { authors: "Whitehurst, Grover J., et al.", title: "Outcomes of an Emergent Literacy Intervention in Head Start", publication: "Journal of Educational Psychology, 1994", annotation: "PEER sequence significantly improves language and early literacy." },
        { authors: "What Works Clearinghouse", title: "Dialogic Reading Intervention Report", publication: "IES, 2007", link: "https://ies.ed.gov/ncee/wwc/Docs/InterventionReports/WWC_Dialogic_Reading_020807.pdf", annotation: "Dialogic reading has positive effects on oral language and print knowledge." },
        { authors: "Wasik, Barbara A., and Mary Alice Bond", title: "Beyond the Pages of a Book: Interactive Book Reading and Language Development in Preschool Classrooms", publication: "Journal of Educational Psychology, 2001", annotation: "Interactive book reading with vocabulary extension activities significantly boosts language outcomes in preschool classrooms." }
      ]
    },
    {
      category: "Bilingual & Digital Learning",
      sources: [
        { authors: "Bialystok, Ellen", title: "Bilingualism in Development", publication: "Cambridge University Press, 2001", annotation: "Bilingual children often have stronger metalinguistic awareness." },
        { authors: "Neumann, Michelle M.", title: "Young Children and Screen Media", publication: "Computers & Education, 2020", annotation: "Screen time alone doesn't help literacy, but co-viewing with discussion can." },
        { authors: "Vaala, Sarah, et al.", title: "Co-Viewing, Co-Playing, Co-Learning", publication: "Joan Ganz Cooney Center, 2015", annotation: "Active parental participation during digital play is key." }
      ]
    }
  ];

  const practitionerResources = [
    { title: 'Reading Rockets', description: 'Phonological awareness, shared reading, vocabulary strategies', link: 'https://www.readingrockets.org/' },
    { title: 'Zero to Three', description: 'Early literacy & language resources for ages 0-3', link: 'https://www.zerotothree.org/resource/what-we-know-about-early-literacy-and-language-development/' },
    { title: 'Every Child Ready to Read (ALA)', description: 'Parent-facing toolkit from emergent literacy research', link: 'https://everychildreadytoread.org/' },
    { title: 'Reach Out and Read', description: 'Clinic-based model for early book exposure', link: 'https://reachoutandread.org/why-we-matter/the-evidence/' },
    { title: 'Put Reading First (NRP Summary)', description: 'Teacher-focused digest of NRP recommendations', link: 'https://lincs.ed.gov/publications/pdf/PRFbooklet.pdf' }
  ];

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-b from-slate-50 via-blue-50 to-indigo-50">
      <nav className="bg-white/80 shadow-sm p-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            {fromTrialIntro && (
              <button
                onClick={() => navigate('/free-activity')}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 transition mr-1 sm:mr-2"
                title="Back to free activity"
              >
                <ArrowLeft size={20} />
                <span className="text-sm sm:text-base font-semibold hidden sm:inline">Back</span>
              </button>
            )}
            <img src="/assets/brlogo2.webp" alt="BrightReading Logo" className="h-8 sm:h-10 w-auto object-contain flex-shrink-0" />
            <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
              BrightReading
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {!fromTrialIntro && (
              <>
                {user && (
                  <button onClick={() => handleNavigate('/dashboard')} className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition" title="Dashboard">
                    <LayoutDashboard size={24} />
                  </button>
                )}
                <button onClick={() => handleNavigate('/')} className="text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition">
                  <Home size={24} />
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4">
            <GraduationCap className="text-white" size={32} />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">The Science Behind BrightReading</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Every activity is built on peer-reviewed research in early literacy. Here's the evidence.
          </p>
        </div>

        {/* Key Findings - Compact grid */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {keyFindings.map((finding, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-md p-5 text-center hover:shadow-lg transition">
              <div className="text-3xl mb-2">{finding.icon}</div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">{finding.title}</h3>
              <p className="text-xs text-gray-600 mb-2">{finding.description}</p>
              <p className="text-xs text-gray-400 italic">{finding.source}</p>
            </div>
          ))}
        </div>

        {/* Research Foundations - Accordion */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Research Foundations</h2>
          <div className="space-y-3">
            {researchFoundations.map((research) => (
              <div key={research.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleSection(research.id)}
                  className={`w-full bg-gradient-to-r ${research.color} p-5 text-left hover:opacity-90 transition`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white/80 text-xs font-semibold">{research.category}</div>
                      <h3 className="text-lg font-bold text-white">{research.title}</h3>
                      <p className="text-white/80 text-sm">{research.summary}</p>
                    </div>
                    <div className="text-white ml-4">
                      {expandedSection === research.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </div>
                </button>

                {expandedSection === research.id && (
                  <div className="p-6">
                    {research.link && (
                      <a href={research.link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg font-semibold text-sm mb-4 hover:bg-blue-100 transition">
                        <ExternalLink size={14} /> View Full Document
                      </a>
                    )}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Lightbulb className="text-yellow-500" size={18} /> Key Findings
                        </h4>
                        <ul className="space-y-2">
                          {research.keyFindings.map((finding, idx) => (
                            <li key={idx} className="flex gap-2 text-gray-700 text-sm">
                              <span className="text-blue-500 font-bold">•</span>
                              <span>{finding}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Award className="text-green-500" size={18} /> How We Apply This
                        </h4>
                        <ul className="space-y-2">
                          {research.applicationInBrightReading.map((app, idx) => (
                            <li key={idx} className="flex gap-2 text-gray-700 text-sm">
                              <span className="text-green-500 font-bold">✓</span>
                              <span>{app}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-6 mb-12">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-black">50+</div>
              <div className="text-xs opacity-80">Studies</div>
            </div>
            <div>
              <div className="text-2xl font-black">50+</div>
              <div className="text-xs opacity-80">Years of Research</div>
            </div>
            <div>
              <div className="text-2xl font-black">100K+</div>
              <div className="text-xs opacity-80">Children Studied</div>
            </div>
            <div>
              <div className="text-2xl font-black">10+</div>
              <div className="text-xs opacity-80">Meta-Analyses</div>
            </div>
          </div>
        </div>

        {/* Annotated Bibliography - Collapsible by category */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center flex items-center justify-center gap-2">
            <BookMarked className="text-purple-600" size={28} />
            Complete Bibliography
          </h2>
          <p className="text-center text-gray-500 text-sm mb-6">Click a category to expand</p>
          <div className="space-y-3">
            {annotatedBibliography.map((section, sectionIdx) => (
              <div key={sectionIdx} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => setExpandedBibSection(expandedBibSection === sectionIdx ? null : sectionIdx)}
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 transition flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-purple-600 font-bold text-sm">{section.sources.length}</span>
                    <h3 className="font-bold text-gray-900">{section.category}</h3>
                  </div>
                  {expandedBibSection === sectionIdx ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                </button>

                {expandedBibSection === sectionIdx && (
                  <div className="px-6 pb-6 space-y-4 border-t border-gray-100 pt-4">
                    {section.sources.map((source, srcIdx) => (
                      <div key={srcIdx} className="pb-4 border-b border-gray-100 last:pb-0 last:border-b-0">
                        <p className="text-xs text-purple-600 font-semibold">{source.authors}</p>
                        <p className="font-bold text-gray-900 text-sm">{source.title}</p>
                        <p className="text-xs text-gray-500 italic mb-1">{source.publication}</p>
                        {source.link && (
                          <a href={source.link} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 text-xs font-semibold mb-1">
                            <ExternalLink size={10} /> Access
                          </a>
                        )}
                        <p className="text-gray-600 text-sm">{source.annotation}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Practitioner Resources - Compact */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Practitioner Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {practitionerResources.map((resource, idx) => (
              <a key={idx} href={resource.link} target="_blank" rel="noopener noreferrer"
                className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition flex items-start gap-3 group">
                <ExternalLink size={16} className="text-purple-600 mt-0.5 flex-shrink-0 group-hover:text-purple-700" />
                <div>
                  <h3 className="font-bold text-gray-900 text-sm group-hover:text-purple-700 transition">{resource.title}</h3>
                  <p className="text-xs text-gray-600">{resource.description}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Why Research Matters */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Why Research-Based Learning Matters</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <TrendingUp className="text-blue-600 mx-auto mb-2" size={28} />
              <h3 className="font-bold text-gray-900 text-sm mb-1">Proven Effective</h3>
              <p className="text-gray-600 text-xs">Measurable improvements across thousands of children</p>
            </div>
            <div className="text-center">
              <Users className="text-green-600 mx-auto mb-2" size={28} />
              <h3 className="font-bold text-gray-900 text-sm mb-1">Diverse Populations</h3>
              <p className="text-gray-600 text-xs">Validated universally across backgrounds</p>
            </div>
            <div className="text-center">
              <Award className="text-purple-600 mx-auto mb-2" size={28} />
              <h3 className="font-bold text-gray-900 text-sm mb-1">Expert Consensus</h3>
              <p className="text-gray-600 text-xs">Recommended by leading organizations and panels</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 sm:p-8 text-center text-white shadow-lg">
          <h2 className="text-2xl font-bold mb-3">Ready to Start?</h2>
          <p className="text-lg mb-6 opacity-95">
            Give your child research-proven early literacy instruction.
          </p>
          <button
            onClick={() => handleNavigate('/dashboard')}
            className="bg-white text-purple-600 px-8 py-3 rounded-full font-bold text-lg hover:bg-gray-50 transition shadow-lg transform hover:scale-105"
          >
            Go to Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResearchPage;
